import React, { useEffect, useState, createElement } from 'react';
import { ChevronRightIcon, CheckIcon, ChevronDownIcon, XIcon, MapPinIcon, InfoIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
interface ProfileSetupProps {
  userType: 'mentor' | 'mentee' | null;
  onComplete: () => void;
  initialStep?: number;
  editMode?: boolean;
  onSave?: (profile: any) => void;
  initialData?: any;
}
interface CustomInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (value: string) => void;
  title: string;
}
const CustomInputModal: React.FC<CustomInputModalProps> = ({
  isOpen,
  onClose,
  onSave,
  title
}) => {
  const [value, setValue] = useState('');
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1897">
      <div className="bg-white rounded-xl w-full max-w-sm p-4" data-id="element-1898">
        <h3 className="font-semibold mb-4" data-id="element-1899">{title}</h3>
        <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 mb-4" value={value} onChange={e => setValue(e.target.value)} placeholder="Please specify" data-id="element-1900" />
        <div className="flex justify-end space-x-2" data-id="element-1901">
          <button className="px-4 py-2 bg-gray-200 rounded-lg" onClick={onClose} data-id="element-1902">
            Cancel
          </button>
          <button className="px-4 py-2 bg-purple-600 text-white rounded-lg" onClick={() => {
          onSave(value);
          onClose();
        }} data-id="element-1903">
            Save
          </button>
        </div>
      </div>
    </div>;
};
const ComboBox: React.FC<{
  options: string[];
  selected: string[];
  onChange: (value: string) => void;
  placeholder: string;
  displayTransform?: (value: string) => string;
}> = ({
  options,
  selected,
  onChange,
  placeholder,
  displayTransform
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const getDisplayValue = (value: string) => {
    return displayTransform ? displayTransform(value) : value;
  };
  return <div className="relative" data-id="element-1904">
      <button type="button" className="w-full px-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between bg-white" onClick={() => setIsOpen(!isOpen)} data-id="element-1905">
        {selected.length > 0 ? <div className="flex flex-wrap gap-1" data-id="element-1906">
            {selected.map(item => <span key={item} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700" data-id="element-1907">
                {getDisplayValue(item)}
                <button onClick={e => {
            e.stopPropagation();
            onChange(item);
          }} className="ml-1 hover:text-purple-900" data-id="element-1908">
                  ×
                </button>
              </span>)}
          </div> : <span className="text-gray-500" data-id="element-1909">{placeholder}</span>}
        <ChevronDownIcon size={16} className="text-gray-400 flex-shrink-0 ml-2" data-id="element-1910" />
      </button>
      {isOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg" data-id="element-1911">
          <div className="p-2 max-h-60 overflow-auto" data-id="element-1912">
            {options.map(option => <button key={option} type="button" className={`w-full px-3 py-2 text-left rounded-lg flex items-center justify-between ${selected.includes(option) ? 'bg-purple-50 text-purple-700' : 'hover:bg-gray-50'}`} onClick={() => {
          onChange(option);
          if (option.includes('Not listed') || option === 'Other') {
            setIsOpen(false);
          }
        }} data-id="element-1913">
                {option}
                {selected.includes(option) && <CheckIcon size={16} className="text-purple-600" data-id="element-1914" />}
              </button>)}
          </div>
        </div>}
    </div>;
};
const ProfileSetup: React.FC<ProfileSetupProps> = ({
  userType,
  onComplete,
  initialStep = 1,
  editMode = false,
  onSave,
  initialData = {}
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(initialStep);
  const [profile, setProfile] = useState({
    name: '',
    pronouns: [] as string[],
    lgbtqAffiliation: [] as string[],
    genderIdentity: [] as string[],
    raceEthnicity: [] as string[],
    accessibility: [] as string[],
    communicationStyle: [] as string[],
    mentorshipReason: '',
    cadence: [] as string[],
    mentorshipCategories: [] as string[],
    location: '',
    agreeToGuidelines: false,
    ...initialData // Merge initial data if provided
  });
  const totalSteps = 4;
  const [showCustomInput, setShowCustomInput] = useState<{
    isOpen: boolean;
    field: string;
    title: string;
  }>({
    isOpen: false,
    field: '',
    title: ''
  });
  const [guidelineChecks, setGuidelineChecks] = useState({
    respect: false,
    confidentiality: false,
    feedback: false,
    report: false,
    engage: false,
    uphold: false
  });
  const [showDisabilityTooltip, setShowDisabilityTooltip] = useState(false);
  const [location, setLocation] = useState(profile.location || '');
  useEffect(() => {
    // Initialize guidelineChecks to true in edit mode
    if (editMode) {
      setGuidelineChecks({
        respect: true,
        confidentiality: true,
        feedback: true,
        report: true,
        engage: true,
        uphold: true
      });
    }
  }, [editMode]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setProfile({
      ...profile,
      [name]: value
    });
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setProfile({
      ...profile,
      [name]: checked
    });
  };
  const handleMultiSelectChange = (field: string, value: string) => {
    const currentValues = profile[field as keyof typeof profile] as string[];
    if (Array.isArray(currentValues)) {
      if (currentValues.includes(value)) {
        setProfile({
          ...profile,
          [field]: currentValues.filter(item => item !== value)
        });
      } else {
        if (field === 'mentorshipCategories' && currentValues.length >= 3 && !currentValues.includes(value)) {
          return;
        }
        setProfile({
          ...profile,
          [field]: [...currentValues, value]
        });
      }
    }
  };
  const isProfileValid = () => {
    if (step === 5) {
      const hasName = profile.name.trim() !== '';
      const hasMentorshipCategory = profile.mentorshipCategories.length > 0;
      const hasAgreedToGuidelines = Object.values(guidelineChecks).every(checked => checked);
      return hasName && hasMentorshipCategory && hasAgreedToGuidelines;
    }
    return true;
  };
  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };
  const nextStep = () => {
    switch (step) {
      case 1:
        if (!profile.name) {
          alert('Please enter your full name');
          return;
        }
        break;
      case 2:
        if (!profile.communicationStyle || !profile.cadence) {
          alert('Please complete all required fields in this step');
          return;
        }
        break;
      case 3:
        if (profile.mentorshipCategories.length === 0) {
          alert('Please select at least one mentorship category');
          return;
        }
        break;
    }
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const hasName = profile.name.trim() !== '';
    const hasMentorshipCategory = profile.mentorshipCategories.length > 0;
    const hasAgreedToGuidelines = Object.values(guidelineChecks).every(checked => checked);
    if (!hasName || !hasMentorshipCategory || !hasAgreedToGuidelines) {
      const missingFields = [];
      if (!hasName) missingFields.push('Full Name');
      if (!hasMentorshipCategory) missingFields.push('At least one mentorship category');
      if (!hasAgreedToGuidelines) missingFields.push('Agreement to community guidelines');
      alert(`Please complete the required fields:\n${missingFields.map(field => `- ${field}`).join('\n')}`);
      return;
    }
    if (editMode && onSave) {
      // In edit mode, call the onSave callback
      onSave(profile);
    } else {
      // In normal mode, complete the onboarding
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsedData = JSON.parse(userData);
        localStorage.setItem('userData', JSON.stringify({
          ...parsedData,
          profile,
          profileComplete: true
        }));
      }
      onComplete();
      navigate(userType === 'mentor' ? '/mentor-dashboard' : '/mentee-dashboard');
    }
  };
  const renderStep = () => {
    switch (step) {
      case 1:
        return <div data-id="element-1915">
            <h3 className="text-lg font-semibold mb-4" data-id="element-1916">Basic Information</h3>
            <div className="mb-4" data-id="element-1917">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-1918">
                Full Name <span className="text-red-500" data-id="element-1919">*</span>
              </label>
              <input type="text" name="name" value={profile.name} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="Enter your full name" required data-id="element-1920" />
              <p className="text-sm text-gray-400 mt-2" data-id="element-1921">
                The following information helps us provide the best mentoring
                experience. Please note: all of the following is not required.
              </p>
            </div>
            <div className="mb-4" data-id="element-1922">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-1923">
                Pronouns
              </label>
              <ComboBox options={['He/Him', 'She/Her', 'They/Them', 'Ze/Zir', 'Not listed (self-describe)']} selected={profile.pronouns} onChange={value => {
              if (value === 'Not listed (self-describe)') {
                setShowCustomInput({
                  isOpen: true,
                  field: 'pronouns',
                  title: 'Specify your pronouns'
                });
              } else {
                handleMultiSelectChange('pronouns', value);
              }
            }} placeholder="Select your pronouns" data-id="element-1924" />
            </div>
            <div className="mb-4" data-id="element-1925">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-1926">
                LGBTQ+ Affiliation
              </label>
              <ComboBox options={['Lesbian', 'Gay', 'Bisexual', 'Transgender', 'Queer', 'Questioning', 'Intersex', 'Asexual', 'Not in the community (ally)', 'Not listed (self-describe)']} selected={profile.lgbtqAffiliation} onChange={value => {
              if (value === 'Not listed (self-describe)') {
                setShowCustomInput({
                  isOpen: true,
                  field: 'lgbtqAffiliation',
                  title: 'Specify your LGBTQ+ affiliation'
                });
              } else {
                handleMultiSelectChange('lgbtqAffiliation', value);
              }
            }} placeholder="Select your LGBTQ+ affiliation" data-id="element-1927" />
            </div>
            <div className="mb-4" data-id="element-1928">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-1929">
                Gender Identity
              </label>
              <ComboBox options={['Cisgender', 'Transgender', 'Non-binary', 'Genderqueer', 'Genderfluid', 'Agender', 'Questioning', 'Prefer not to say', 'Not listed (self-describe)']} selected={profile.genderIdentity} onChange={value => {
              if (value === 'Not listed (self-describe)') {
                setShowCustomInput({
                  isOpen: true,
                  field: 'genderIdentity',
                  title: 'Specify your gender identity'
                });
              } else {
                handleMultiSelectChange('genderIdentity', value);
              }
            }} placeholder="Select your gender identity" data-id="element-1930" />
            </div>
            <div className="mb-4" data-id="element-1931">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-1932">
                Race & Ethnicity
              </label>
              <ComboBox options={['Asian', 'Black or African American', 'Hispanic or Latino/a/x', 'Middle Eastern or North African', 'Native American or Alaska Native', 'Native Hawaiian or Pacific Islander', 'White', 'Multiracial', 'Prefer not to say', 'Not listed (self-describe)']} selected={profile.raceEthnicity} onChange={value => {
              if (value === 'Not listed (self-describe)') {
                setShowCustomInput({
                  isOpen: true,
                  field: 'raceEthnicity',
                  title: 'Specify your race and ethnicity'
                });
              } else {
                handleMultiSelectChange('raceEthnicity', value);
              }
            }} placeholder="Select your race and ethnicity" data-id="element-1933" />
            </div>
            <div className="mb-4" data-id="element-1934">
              <div className="flex items-center mb-1" data-id="element-1935">
                <label className="block text-gray-700 text-sm font-medium" data-id="element-1936">
                  Disability Status
                </label>
                <button type="button" className="ml-2" onMouseEnter={() => setShowDisabilityTooltip(true)} onMouseLeave={() => setShowDisabilityTooltip(false)} data-id="element-1937">
                  <InfoIcon size={16} className="text-gray-400" data-id="element-1938" />
                </button>
              </div>
              {showDisabilityTooltip && <div className="text-xs text-gray-600 bg-gray-100 p-2 rounded-lg mb-2" data-id="element-1939">
                  If you need additional accessibility help, please contact us{' '}
                  <a href="#" className="text-purple-600 hover:underline" data-id="element-1940">
                    here
                  </a>
                </div>}
              <ComboBox options={['ADHD', 'Autism Spectrum', 'Dyslexia', 'Visual Impairment', 'Hearing Impairment', 'Mobility Disability', 'Cognitive Disability', 'Mental Health Condition', 'Speech or Language Disability', 'Chronic Health Condition', 'Prefer not to say', 'Not listed (self-describe)']} selected={profile.accessibility} onChange={value => {
              if (value === 'Not listed (self-describe)') {
                setShowCustomInput({
                  isOpen: true,
                  field: 'accessibility',
                  title: 'Specify your accessibility needs'
                });
              } else {
                handleMultiSelectChange('accessibility', value);
              }
            }} placeholder="Select your disability status" data-id="element-1941" />
            </div>
          </div>;
      case 2:
        return <div data-id="element-1942">
            <h3 className="text-lg font-semibold mb-4" data-id="element-1943">
              Communication Preferences
            </h3>
            <div className="mb-4" data-id="element-1944">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-1945">
                Why do you want to be a {userType}?
              </label>
              <textarea name="mentorshipReason" value={profile.mentorshipReason} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder={`Share why you want to ${userType === 'mentor' ? 'mentor others' : 'receive mentorship'}`} rows={4} required data-id="element-1946" />
            </div>
            <div className="mb-4" data-id="element-1947">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-1948">
                Communication Style
              </label>
              <ComboBox options={['Direct - Get straight to the point', 'Collaborative - Work through ideas together', 'Visual - Use images and diagrams', 'Detailed - Provide comprehensive information', 'Empathetic - Focus on feelings and relationships']} selected={profile.communicationStyle} onChange={value => handleMultiSelectChange('communicationStyle', value)} placeholder="Select your communication style" displayTransform={value => value.split(' - ')[0]} data-id="element-1949" />
            </div>
            <div className="mb-4" data-id="element-1950">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-1951">
                Preferred Meeting Cadence
              </label>
              <ComboBox options={['Weekly', 'Bi-weekly', 'Monthly', 'As needed']} selected={profile.cadence} onChange={value => handleMultiSelectChange('cadence', value)} placeholder="Select your preferred cadence" data-id="element-1952" />
            </div>
            <div className="mb-4" data-id="element-1953">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-1954">
                Location
              </label>
              <div className="relative" data-id="element-1955">
                <input type="text" value={location} onChange={e => {
                setLocation(e.target.value);
                setProfile({
                  ...profile,
                  location: e.target.value
                });
              }} className="w-full pl-3 pr-24 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="Enter your location" data-id="element-1956" />
                <button type="button" onClick={handleLocationDetection} className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-purple-100 text-purple-600 rounded-lg text-sm flex items-center" data-id="element-1957">
                  <MapPinIcon size={14} className="mr-1" data-id="element-1958" />
                  Locate Me
                </button>
              </div>
            </div>
          </div>;
      case 3:
        return <div data-id="element-1959">
            <h3 className="text-lg font-semibold mb-4" data-id="element-1960">
              Mentorship Categories
            </h3>
            <p className="text-gray-600 text-sm mb-4" data-id="element-1961">
              Select 1-3 categories you're interested in{' '}
              {userType === 'mentor' ? 'mentoring for' : 'receiving mentorship in'}
            </p>
            <div className="grid grid-cols-2 gap-3" data-id="element-1962">
              {[{
              name: 'Career',
              emoji: '💼'
            }, {
              name: 'Academic',
              emoji: '🎓'
            }, {
              name: 'Creative',
              emoji: '🎨'
            }, {
              name: 'Wellness',
              emoji: '💪'
            }, {
              name: 'Growth',
              emoji: '🌱'
            }, {
              name: 'Finance',
              emoji: '🧾'
            }, {
              name: 'Identity',
              emoji: '🧭'
            }, {
              name: 'Community',
              emoji: '🫶'
            }, {
              name: 'Focus',
              emoji: '🧠'
            }, {
              name: 'Goals',
              emoji: '🎯'
            }, {
              name: 'Life',
              emoji: '🧳'
            }, {
              name: 'Parenting',
              emoji: '🍼'
            }].map(category => <button key={category.name} type="button" onClick={() => handleMultiSelectChange('mentorshipCategories', category.name)} disabled={profile.mentorshipCategories.length >= 3 && !profile.mentorshipCategories.includes(category.name)} className={`py-3 px-4 border rounded-lg flex items-center justify-between ${profile.mentorshipCategories.includes(category.name) ? 'bg-purple-100 border-purple-500 text-purple-700' : profile.mentorshipCategories.length >= 3 && !profile.mentorshipCategories.includes(category.name) ? 'border-gray-200 text-gray-400' : 'border-gray-300 text-gray-700'}`} data-id="element-1963">
                  <span data-id="element-1964">
                    {category.emoji} {category.name}
                  </span>
                  {profile.mentorshipCategories.includes(category.name) && <CheckIcon size={16} className="text-purple-600" data-id="element-1965" />}
                </button>)}
            </div>
            <div className="mt-4 text-sm text-gray-500" data-id="element-1966">
              {profile.mentorshipCategories.length}/3 categories selected
            </div>
          </div>;
      case 4:
        return <div data-id="element-1967">
            <h3 className="text-lg font-semibold mb-4 text-purple-600" data-id="element-1968">
              Community Guidelines
            </h3>
            <div className="p-4 bg-gray-50 rounded-lg mb-4" data-id="element-1969">
              <p className="text-sm text-gray-700 mb-3" data-id="element-1970">
                Our community is built on respect, inclusivity, and support. By
                joining PrideMentor, you agree to:
              </p>
              <ul className="space-y-3" data-id="element-1971">
                {[{
                id: 'respect',
                text: 'Treat all members with respect and dignity'
              }, {
                id: 'confidentiality',
                text: 'Maintain confidentiality of private discussions'
              }, {
                id: 'feedback',
                text: 'Provide constructive feedback and support'
              }, {
                id: 'report',
                text: 'Report any inappropriate behavior'
              }, {
                id: 'engage',
                text: 'Engage actively in the mentorship relationship'
              }, {
                id: 'uphold',
                text: 'Uphold the values of the LGBTQ+ community'
              }].map(({
                id,
                text
              }) => <li key={id} className="flex items-start" data-id="element-1972">
                    <div className="flex items-center" data-id="element-1973">
                      <input type="checkbox" id={id} checked={guidelineChecks[id as keyof typeof guidelineChecks]} onChange={e => {
                    setGuidelineChecks(prev => ({
                      ...prev,
                      [id]: e.target.checked
                    }));
                  }} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-1974" />
                      <label htmlFor={id} className={`ml-2 text-sm transition-colors duration-200 ${guidelineChecks[id as keyof typeof guidelineChecks] ? 'text-purple-600' : 'text-gray-700'}`} data-id="element-1975">
                        {text}
                      </label>
                    </div>
                  </li>)}
              </ul>
            </div>
            <div className="text-xs text-gray-500 mb-4" data-id="element-1976">
              <p data-id="element-1977">
                By checking all boxes above and clicking "All Done!", you
                acknowledge that you have read and agree to our{' '}
                <a href="#" className="text-purple-600 hover:underline" data-id="element-1978">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="#" className="text-purple-600 hover:underline" data-id="element-1979">
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </div>;
      default:
        return null;
    }
  };
  const handleLocationDetection = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async position => {
        try {
          const locationStr = `${position.coords.latitude}, ${position.coords.longitude}`;
          setLocation(locationStr);
          setProfile({
            ...profile,
            location: locationStr
          });
        } catch (error) {
          console.error('Error getting location:', error);
        }
      });
    }
  };
  return <div className={`${editMode ? '' : 'min-h-screen bg-gray-50 p-4'}`} data-id="element-1980">
      <div className={`${editMode ? '' : 'max-w-md mx-auto'}`} data-id="element-1981">
        <div className={`bg-white ${editMode ? 'rounded-lg' : 'rounded-xl shadow-md'} overflow-hidden`} data-id="element-1982">
          {!editMode && <>
              <div className="bg-purple-600 px-4 py-3 text-white flex justify-between items-center" data-id="element-1983">
                <h2 className="text-lg font-semibold" data-id="element-1984">Set Up Your Profile</h2>
                <div className="text-sm" data-id="element-1985">
                  Step {step}/{totalSteps}
                </div>
              </div>
              <div className="w-full bg-gray-200 h-1" data-id="element-1986">
                <div className="bg-purple-600 h-1 transition-all duration-300" style={{
              width: `${step / totalSteps * 100}%`
            }} data-id="element-1987"></div>
              </div>
            </>}
          <form onSubmit={handleSubmit} className="p-4" data-id="element-1988">
            {renderStep()}
            <div className="flex justify-between mt-6" data-id="element-1989">
              {step > 1 ? <button type="button" onClick={prevStep} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50" data-id="element-1990">
                  Back
                </button> : <div data-id="element-1991"></div>}
              {step < totalSteps ? <button type="button" onClick={nextStep} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center" data-id="element-1992">
                  Next <ChevronRightIcon size={16} className="ml-1" data-id="element-1993" />
                </button> : <button type="submit" disabled={!Object.values(guidelineChecks).every(checked => checked)} className={`px-6 py-3 rounded-lg flex items-center justify-center text-lg font-semibold ${Object.values(guidelineChecks).every(checked => checked) ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-id="element-1994">
                  {editMode ? 'Save Changes' : 'All Done!'}
                </button>}
            </div>
          </form>
        </div>
      </div>
      <CustomInputModal isOpen={showCustomInput.isOpen} onClose={() => setShowCustomInput({
      isOpen: false,
      field: '',
      title: ''
    })} onSave={value => {
      handleMultiSelectChange(showCustomInput.field, value);
    }} title={showCustomInput.title} data-id="element-1995" />
    </div>;
};
const styles = `
  @keyframes pop {
    0% { transform: scale(0); opacity: 0; }
    50% { transform: scale(1.2); opacity: 0.7; }
    100% { transform: scale(1); opacity: 1; }
  }
  .animate-pop {
    animation: pop 0.3s ease-out forwards;
  }
`;
const styleSheet = document.createElement('style');
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);
export default ProfileSetup;